import React, { useState } from "react";
import {
  Box, Container, Heading, Text, Input, Checkbox, Button,
  VStack, HStack, Grid, GridItem, FormControl, FormLabel,
  Divider, useToast, Image, Table, Thead, Tbody, Tr, Th, Td,
  TableContainer, Textarea,
} from "@chakra-ui/react";
import { FaDownload, FaPrint, FaSun } from "react-icons/fa";
import { generatePropertyTransactionPDF } from "./components/generatePropertyTransactionPDF";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { brandConfig } from "../../../brandConfig";

// ─────────────────────────────────────────────
// INTERFACE
// ─────────────────────────────────────────────
export interface PropertyTransactionFormData {
  // APPLICANT A
  nameA: string; dobA: string; maritalStatusA: string;
  addressA: string; residenceYearsA: string; stateA: string; postcodeA: string;
  phoneHomeA: string; mobileA: string; emailA: string; tfnA: string;
  occupationA: string; employerA: string; employerAddressA: string;
  empFITA: boolean; empPTA: boolean; empCasualA: boolean; empSelfA: boolean;
  employmentYearsA: string; grossIncomeA: string;
  otherIncomeA: string; otherIncomeTypeA: string;
  selfEmpBusinessNameA: string; selfEmpABNA: string; selfEmpYearsA: string;
  dependentsA: string; dependentAgesA: string;

  // APPLICANT B
  nameB: string; dobB: string; maritalStatusB: string;
  addressB: string; residenceYearsB: string; stateB: string; postcodeB: string;
  phoneHomeB: string; mobileB: string; emailB: string; tfnB: string;
  occupationB: string; employerB: string; employerAddressB: string;
  empFITB: boolean; empPTB: boolean; empCasualB: boolean; empSelfB: boolean;
  employmentYearsB: string; grossIncomeB: string;
  otherIncomeB: string; otherIncomeTypeB: string;
  selfEmpBusinessNameB: string; selfEmpABNB: string; selfEmpYearsB: string;
  dependentsB: string; dependentAgesB: string;

  // PROPERTY DETAILS
  propertyAddress: string; propertySuburb: string;
  propertyState: string; propertyPostcode: string;
  isHouse: boolean; isUnit: boolean; isTownhouse: boolean;
  isDuplex: boolean; isLand: boolean; isCommercial: boolean;
  bedrooms: string; bathrooms: string; carSpaces: string;
  landSize: string; floorArea: string; yearBuilt: string;
  bodyCorpYes: boolean; bodyCorpNo: boolean; bodyCorpFees: string;

  // PURCHASE DETAILS
  purchasePrice: string; depositAmount: string;
  depositPaidYes: boolean; depositPaidNo: boolean;
  depositCash: boolean; depositEquity: boolean; depositFHSS: boolean; depositGift: boolean;
  settlementDate: string; exchangeDate: string;
  firstHomeBuyerYes: boolean; firstHomeBuyerNo: boolean;

  // PROPERTY PURPOSE
  purposePPOR: boolean; purposeDirectInvestment: boolean; purposeSMSF: boolean;
  estimatedRental: string;
  propertyManagedYes: boolean; propertyManagedNo: boolean;
  propertyManagerName: string; propertyManagerPhone: string;

  // REAL ESTATE AGENT
  agentName: string; agentAgency: string; agentPhone: string; agentEmail: string;

  // SOLICITOR / CONVEYANCER
  solicitorName: string; solicitorFirm: string;
  solicitorPhone: string; solicitorEmail: string;

  // FINANCE & LOAN
  loanAmountRequested: string; lvr: string;
  loanPurposeOO: boolean; loanPurposeInvestment: boolean;
  loanTypePI: boolean; loanTypeIO: boolean;
  ioPeriod: string; loanTerm: string;
  rateVariable: boolean; rateFixed: boolean; rateSplit: boolean;
  fixedPeriod: string;
  offsetYes: boolean; offsetNo: boolean;
  redrawYes: boolean; redrawNo: boolean;
  lenderPreference: string;
  brokerName: string; brokerCompany: string; brokerPhone: string; brokerACL: string;

  // LIVING EXPENSES (monthly)
  expGroceries: string; expTransport: string; expUtilities: string;
  expPhoneInternet: string; expInsurance: string; expMedical: string;
  expEntertainment: string; expChildcare: string; expEducation: string;
  expOtherMonthly: string;

  // CREDIT HISTORY
  defaultsYes: boolean; defaultsNo: boolean; defaultDetails: string;
  bankruptcyYes: boolean; bankruptcyNo: boolean; bankruptcyDetails: string;
  judgmentsYes: boolean; judgmentsNo: boolean; judgmentDetails: string;

  // SMSF DETAILS
  smsfFundName: string; smsfABN: string; smsfTFN: string;
  smsfTrusteeIndividual: boolean; smsfTrusteeCorporate: boolean;
  smsfCorpTrusteeName: string; smsfCorpTrusteeACN: string;
  smsfMember1Name: string; smsfMember1DOB: string; smsfMember1Balance: string;
  smsfMember2Name: string; smsfMember2DOB: string; smsfMember2Balance: string;
  smsfTotalBalance: string; smsfAnnualContributions: string;
  smsfLRBAYes: boolean; smsfLRBANo: boolean;
  smsfBareTrustName: string; smsfAuditorName: string;
  smsfCompliantYes: boolean; smsfCompliantNo: boolean;

  // ASSETS
  assetPPORValue: string; assetPPOROwner: string; assetPPORRent: string;
  assetIP1Value: string; assetIP1Owner: string; assetIP1Rent: string;
  assetIP2Value: string; assetIP2Owner: string; assetIP2Rent: string;
  assetIP3Value: string; assetIP3Owner: string; assetIP3Rent: string;
  assetSMSFPropValue: string; assetSMSFPropOwner: string;
  assetSuperValue: string; assetSuperOwner: string;
  assetSharesValue: string; assetSharesOwner: string;
  assetSavingsValue: string; assetSavingsOwner: string;
  assetVehicle1Value: string; assetVehicle1Owner: string;
  assetVehicle2Value: string; assetVehicle2Owner: string;
  assetBusinessValue: string; assetBusinessOwner: string;
  assetOther1Value: string; assetOther1Owner: string;
  assetOther2Value: string; assetOther2Owner: string;

  // LIABILITIES
  liabMortgage1Lender: string; liabMortgage1Balance: string; liabMortgage1Limit: string; liabMortgage1Repayment: string;
  liabMortgage2Lender: string; liabMortgage2Balance: string; liabMortgage2Limit: string; liabMortgage2Repayment: string;
  liabMortgage3Lender: string; liabMortgage3Balance: string; liabMortgage3Limit: string; liabMortgage3Repayment: string;
  liabCarLender: string; liabCarBalance: string; liabCarLimit: string; liabCarRepayment: string;
  liabPersonalLender: string; liabPersonalBalance: string; liabPersonalLimit: string; liabPersonalRepayment: string;
  liabCC1Lender: string; liabCC1Balance: string; liabCC1Limit: string; liabCC1Repayment: string;
  liabCC2Lender: string; liabCC2Balance: string; liabCC2Limit: string; liabCC2Repayment: string;
  liabHECSBalance: string; liabHECSRepayment: string;
  liabPrivateHealthRepayment: string;
  liabPrivateSchoolRepayment: string;
  liabChildCareRepayment: string;
  liabOther1Lender: string; liabOther1Balance: string; liabOther1Limit: string; liabOther1Repayment: string;
  liabOther2Lender: string; liabOther2Balance: string; liabOther2Limit: string; liabOther2Repayment: string;

  // INSURANCE
  buildingInsYes: boolean; buildingInsNo: boolean; buildingInsurer: string; buildingPolicyNo: string; buildingSum: string;
  contentsInsYes: boolean; contentsInsNo: boolean; contentsInsurer: string; contentsPolicyNo: string;
  landlordInsYes: boolean; landlordInsNo: boolean; landlordInsurer: string; landlordPolicyNo: string;
  lifeInsYes: boolean; lifeInsNo: boolean; lifeInsurer: string; lifeSumInsured: string;
  incomeProtYes: boolean; incomeProtNo: boolean; incomeProtInsurer: string;

  // UTILITIES & SOLAR CROSS-SELL
  electricityProvider: string; electricityBillMonthly: string;
  hasWaterFilter: boolean; noWaterFilter: boolean;
  solarInterestYes: boolean; solarInterestNo: boolean;
  solarApptName: string; solarApptPhone: string; solarApptPreferredTime: string;

  // DOCUMENT CHECKLIST
  docPayslips: boolean; doc2YrTaxReturns: boolean; doc3MthBankStatements: boolean;
  docIDVerified: boolean; docCentrelink: boolean; docRentalStatement: boolean;
  docContractOfSale: boolean; docBuildingReport: boolean; docPestReport: boolean;
  docCouncilRates: boolean; docBodyCorp: boolean;
  docSmsfDeed: boolean; docSmsfAuditLetter: boolean;
  docSmsfTrusteeResolution: boolean; docBareTrustDeed: boolean;

  // SIGNATURES
  consultantName: string; consultantNumber: string; date: string;
}

// ─────────────────────────────────────────────
// EMPTY FORM DATA
// ─────────────────────────────────────────────
const empty: PropertyTransactionFormData = {
  nameA: "", dobA: "", maritalStatusA: "", addressA: "", residenceYearsA: "",
  stateA: "", postcodeA: "", phoneHomeA: "", mobileA: "", emailA: "", tfnA: "",
  occupationA: "", employerA: "", employerAddressA: "",
  empFITA: false, empPTA: false, empCasualA: false, empSelfA: false,
  employmentYearsA: "", grossIncomeA: "", otherIncomeA: "", otherIncomeTypeA: "",
  selfEmpBusinessNameA: "", selfEmpABNA: "", selfEmpYearsA: "",
  dependentsA: "", dependentAgesA: "",

  nameB: "", dobB: "", maritalStatusB: "", addressB: "", residenceYearsB: "",
  stateB: "", postcodeB: "", phoneHomeB: "", mobileB: "", emailB: "", tfnB: "",
  occupationB: "", employerB: "", employerAddressB: "",
  empFITB: false, empPTB: false, empCasualB: false, empSelfB: false,
  employmentYearsB: "", grossIncomeB: "", otherIncomeB: "", otherIncomeTypeB: "",
  selfEmpBusinessNameB: "", selfEmpABNB: "", selfEmpYearsB: "",
  dependentsB: "", dependentAgesB: "",

  propertyAddress: "", propertySuburb: "", propertyState: "", propertyPostcode: "",
  isHouse: false, isUnit: false, isTownhouse: false, isDuplex: false, isLand: false, isCommercial: false,
  bedrooms: "", bathrooms: "", carSpaces: "", landSize: "", floorArea: "", yearBuilt: "",
  bodyCorpYes: false, bodyCorpNo: false, bodyCorpFees: "",

  purchasePrice: "", depositAmount: "",
  depositPaidYes: false, depositPaidNo: false,
  depositCash: false, depositEquity: false, depositFHSS: false, depositGift: false,
  settlementDate: "", exchangeDate: "",
  firstHomeBuyerYes: false, firstHomeBuyerNo: false,

  purposePPOR: false, purposeDirectInvestment: false, purposeSMSF: false,
  estimatedRental: "", propertyManagedYes: false, propertyManagedNo: false,
  propertyManagerName: "", propertyManagerPhone: "",

  agentName: "", agentAgency: "", agentPhone: "", agentEmail: "",
  solicitorName: "", solicitorFirm: "", solicitorPhone: "", solicitorEmail: "",

  loanAmountRequested: "", lvr: "",
  loanPurposeOO: false, loanPurposeInvestment: false,
  loanTypePI: false, loanTypeIO: false, ioPeriod: "", loanTerm: "",
  rateVariable: false, rateFixed: false, rateSplit: false, fixedPeriod: "",
  offsetYes: false, offsetNo: false, redrawYes: false, redrawNo: false,
  lenderPreference: "", brokerName: "", brokerCompany: "", brokerPhone: "", brokerACL: "",

  expGroceries: "", expTransport: "", expUtilities: "", expPhoneInternet: "",
  expInsurance: "", expMedical: "", expEntertainment: "", expChildcare: "",
  expEducation: "", expOtherMonthly: "",

  defaultsYes: false, defaultsNo: false, defaultDetails: "",
  bankruptcyYes: false, bankruptcyNo: false, bankruptcyDetails: "",
  judgmentsYes: false, judgmentsNo: false, judgmentDetails: "",

  smsfFundName: "", smsfABN: "", smsfTFN: "",
  smsfTrusteeIndividual: false, smsfTrusteeCorporate: false,
  smsfCorpTrusteeName: "", smsfCorpTrusteeACN: "",
  smsfMember1Name: "", smsfMember1DOB: "", smsfMember1Balance: "",
  smsfMember2Name: "", smsfMember2DOB: "", smsfMember2Balance: "",
  smsfTotalBalance: "", smsfAnnualContributions: "",
  smsfLRBAYes: false, smsfLRBANo: false,
  smsfBareTrustName: "", smsfAuditorName: "",
  smsfCompliantYes: false, smsfCompliantNo: false,

  assetPPORValue: "", assetPPOROwner: "", assetPPORRent: "",
  assetIP1Value: "", assetIP1Owner: "", assetIP1Rent: "",
  assetIP2Value: "", assetIP2Owner: "", assetIP2Rent: "",
  assetIP3Value: "", assetIP3Owner: "", assetIP3Rent: "",
  assetSMSFPropValue: "", assetSMSFPropOwner: "",
  assetSuperValue: "", assetSuperOwner: "",
  assetSharesValue: "", assetSharesOwner: "",
  assetSavingsValue: "", assetSavingsOwner: "",
  assetVehicle1Value: "", assetVehicle1Owner: "",
  assetVehicle2Value: "", assetVehicle2Owner: "",
  assetBusinessValue: "", assetBusinessOwner: "",
  assetOther1Value: "", assetOther1Owner: "",
  assetOther2Value: "", assetOther2Owner: "",

  liabMortgage1Lender: "", liabMortgage1Balance: "", liabMortgage1Limit: "", liabMortgage1Repayment: "",
  liabMortgage2Lender: "", liabMortgage2Balance: "", liabMortgage2Limit: "", liabMortgage2Repayment: "",
  liabMortgage3Lender: "", liabMortgage3Balance: "", liabMortgage3Limit: "", liabMortgage3Repayment: "",
  liabCarLender: "", liabCarBalance: "", liabCarLimit: "", liabCarRepayment: "",
  liabPersonalLender: "", liabPersonalBalance: "", liabPersonalLimit: "", liabPersonalRepayment: "",
  liabCC1Lender: "", liabCC1Balance: "", liabCC1Limit: "", liabCC1Repayment: "",
  liabCC2Lender: "", liabCC2Balance: "", liabCC2Limit: "", liabCC2Repayment: "",
  liabHECSBalance: "", liabHECSRepayment: "",
  liabPrivateHealthRepayment: "", liabPrivateSchoolRepayment: "", liabChildCareRepayment: "",
  liabOther1Lender: "", liabOther1Balance: "", liabOther1Limit: "", liabOther1Repayment: "",
  liabOther2Lender: "", liabOther2Balance: "", liabOther2Limit: "", liabOther2Repayment: "",

  buildingInsYes: false, buildingInsNo: false, buildingInsurer: "", buildingPolicyNo: "", buildingSum: "",
  contentsInsYes: false, contentsInsNo: false, contentsInsurer: "", contentsPolicyNo: "",
  landlordInsYes: false, landlordInsNo: false, landlordInsurer: "", landlordPolicyNo: "",
  lifeInsYes: false, lifeInsNo: false, lifeInsurer: "", lifeSumInsured: "",
  incomeProtYes: false, incomeProtNo: false, incomeProtInsurer: "",

  electricityProvider: "", electricityBillMonthly: "",
  hasWaterFilter: false, noWaterFilter: false,
  solarInterestYes: false, solarInterestNo: false,
  solarApptName: "", solarApptPhone: "", solarApptPreferredTime: "",

  docPayslips: false, doc2YrTaxReturns: false, doc3MthBankStatements: false,
  docIDVerified: false, docCentrelink: false, docRentalStatement: false,
  docContractOfSale: false, docBuildingReport: false, docPestReport: false,
  docCouncilRates: false, docBodyCorp: false,
  docSmsfDeed: false, docSmsfAuditLetter: false,
  docSmsfTrusteeResolution: false, docBareTrustDeed: false,

  consultantName: "", consultantNumber: "", date: "",
};

// ─────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────
const PropertyTransactionForm: React.FC = () => {
  const toast = useToast();
  const [formData, setFormData] = useState<PropertyTransactionFormData>(empty);

  const set = (field: keyof PropertyTransactionFormData, value: string | boolean) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handlePrint = () => window.print();

  const handleDownloadPDF = async () => {
    toast({ title: "Generating PDF", description: "Building your transaction form...", status: "info", duration: 3000, isClosable: true });
    try {
      await generatePropertyTransactionPDF(formData);
      toast({ title: "PDF Downloaded", description: "Property Transaction Form saved.", status: "success", duration: 3000, isClosable: true });
    } catch (err) {
      console.error(err);
      toast({ title: "PDF Error", description: "Could not generate PDF. Please try again.", status: "error", duration: 5000, isClosable: true });
    }
  };

  // ── Shared styles ──
  const inp = {
    bg: brandConfig.components.form.fieldBg,
    border: "1px solid",
    borderColor: "rgba(0,0,0,0.12)",
    _hover: { borderColor: "rgba(0,0,0,0.25)" },
    _focus: { borderColor: brandConfig.components.form.fieldBorderFocus, boxShadow: brandConfig.components.form.fieldShadowFocus },
  };
  const thS = { bg: brandConfig.colors.primary, color: "white", fontSize: "xs", fontWeight: "bold", textAlign: "center" as const, px: 2, py: 1 };
  const tdL = { fontSize: "xs", fontWeight: "semibold", color: brandConfig.colors.text.primary, whiteSpace: "nowrap" as const, px: 2, py: 1 };
  const tdI = { px: 1, py: 1 };

  // ── Reusable section heading ──
  const SH = ({ title }: { title: string }) => (
    <Box bg={brandConfig.colors.primary} px={3} py={1} mb={4} borderRadius="sm">
      <Text color="white" fontWeight="bold" fontSize="sm">{title}</Text>
    </Box>
  );

  // ── Labeled input shorthand ──
  const LI = ({ label, field, placeholder, size = "sm" }: { label: string; field: keyof PropertyTransactionFormData; placeholder?: string; size?: string }) => (
    <FormControl>
      <FormLabel fontSize="xs">{label}</FormLabel>
      <Input value={formData[field] as string} onChange={(e) => set(field, e.target.value)} {...inp} size={size as any} placeholder={placeholder} />
    </FormControl>
  );

  // ── Asset row ──
  const AssetRow = ({ label, vf, of, rf }: { label: string; vf: keyof PropertyTransactionFormData; of: keyof PropertyTransactionFormData; rf?: keyof PropertyTransactionFormData }) => (
    <Tr>
      <Td {...tdL}>{label}</Td>
      <Td {...tdI}><Input value={formData[vf] as string} onChange={(e) => set(vf, e.target.value)} {...inp} size="xs" placeholder="$" /></Td>
      <Td {...tdI}><Input value={formData[of] as string} onChange={(e) => set(of, e.target.value)} {...inp} size="xs" /></Td>
      <Td {...tdI}>{rf ? <Input value={formData[rf] as string} onChange={(e) => set(rf, e.target.value)} {...inp} size="xs" placeholder="$" /> : <Box h="24px" bg="gray.100" borderRadius="md" />}</Td>
    </Tr>
  );

  // ── Liability row ──
  const LiabRow = ({ label, lf, bf, limf, rf }: { label: string; lf: keyof PropertyTransactionFormData; bf: keyof PropertyTransactionFormData; limf: keyof PropertyTransactionFormData; rf: keyof PropertyTransactionFormData }) => (
    <Tr>
      <Td {...tdL}>{label}</Td>
      <Td {...tdI}><Input value={formData[lf] as string} onChange={(e) => set(lf, e.target.value)} {...inp} size="xs" /></Td>
      <Td {...tdI}><Input value={formData[bf] as string} onChange={(e) => set(bf, e.target.value)} {...inp} size="xs" placeholder="$" /></Td>
      <Td {...tdI}><Input value={formData[limf] as string} onChange={(e) => set(limf, e.target.value)} {...inp} size="xs" placeholder="$" /></Td>
      <Td {...tdI}><Input value={formData[rf] as string} onChange={(e) => set(rf, e.target.value)} {...inp} size="xs" placeholder="$" /></Td>
    </Tr>
  );

  // ── Expense row (no lender/limit cols needed) ──
  const ExpRow = ({ label, rf }: { label: string; rf: keyof PropertyTransactionFormData }) => (
    <Tr>
      <Td {...tdL}>{label}</Td>
      <Td {...tdI} colSpan={4}><Input value={formData[rf] as string} onChange={(e) => set(rf, e.target.value)} {...inp} size="xs" placeholder="$ per month" /></Td>
    </Tr>
  );

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/resources/transaction-form"
        fallbackTitle="Property Transaction & Mortgage Form"
        fallbackDescription="Comprehensive property transaction and mortgage application form for One Property Australia"
      />

      <Box className="no-print"><NavbarWithCallToAction /></Box>

      <Container maxW="container.xl" py={8}>
        {/* Action Buttons */}
        <HStack spacing={4} mb={6} className="no-print">
          <Button leftIcon={<FaPrint />} bg={brandConfig.colors.primary} color="white" _hover={{ bg: brandConfig.colors.primaryHover }} onClick={handlePrint}>
            Print Form
          </Button>
          <Button leftIcon={<FaDownload />} bg="#0D7F6E" color="white" _hover={{ bg: "#0a6459" }} onClick={handleDownloadPDF}>
            Download as PDF
          </Button>
        </HStack>

        {/* ═══════════════════════ PRINTABLE FORM ═══════════════════════ */}
        <Box id="ptf-printable" bg="white" p={8} border="1px solid" borderColor="rgba(0,0,0,0.1)" borderRadius="md">

          {/* HEADER */}
          <HStack justify="space-between" mb={6} align="flex-start">
            <Box flexShrink={0}>
              <Image src="/assets/One-logo.png" alt="One Property Australia" h="80px" w="auto" objectFit="contain" />
            </Box>
            <VStack spacing={1} flex={1} alignSelf="center">
              <Heading size="lg" textAlign="center">PROPERTY TRANSACTION &amp; MORTGAGE FORM</Heading>
              <Text fontSize="sm" color={brandConfig.colors.text.secondary} textAlign="center">Direct Investment &amp; SMSF</Text>
            </VStack>
            <VStack align="flex-end" spacing={0} fontSize="xs" flexShrink={0}>
              <Text fontWeight="bold">One Property Australia</Text>
              <Text>ACN: 664 898 846</Text>
              <Text>6/22 Alexandra Pl, Murarrie QLD 4172</Text>
              <Text>ONEPROPERTYAUSTRALIA.COM.AU</Text>
            </VStack>
          </HStack>

          <Divider my={4} />

          {/* ══════════════════════════════════════════════
              SECTION 1 — APPLICANT DETAILS
          ══════════════════════════════════════════════ */}
          <SH title="SECTION 1 — APPLICANT DETAILS" />
          <Grid templateColumns="1fr 1fr" gap={6} mb={4}>

            {/* Applicant A */}
            <GridItem>
              <Text fontWeight="bold" fontSize="sm" color={brandConfig.colors.primary} mb={3}>Applicant A</Text>
              <VStack spacing={3} align="stretch">
                <LI label="Full Name:" field="nameA" />
                <Grid templateColumns="1fr 1fr" gap={2}>
                  <LI label="Date of Birth:" field="dobA" placeholder="DD/MM/YYYY" />
                  <LI label="Marital Status:" field="maritalStatusA" />
                </Grid>
                <LI label="Residential Address:" field="addressA" />
                <Grid templateColumns="1fr 1fr 1fr" gap={2}>
                  <LI label="Yrs at Address:" field="residenceYearsA" />
                  <LI label="State:" field="stateA" />
                  <LI label="Postcode:" field="postcodeA" />
                </Grid>
                <Grid templateColumns="1fr 1fr" gap={2}>
                  <LI label="Phone (H):" field="phoneHomeA" />
                  <LI label="Mobile:" field="mobileA" />
                </Grid>
                <LI label="Email:" field="emailA" />
                <LI label="Tax File Number:" field="tfnA" />
                <LI label="Occupation:" field="occupationA" />
                <LI label="Employer:" field="employerA" />
                <LI label="Employer Address:" field="employerAddressA" />
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="bold">Employment Type:</FormLabel>
                  <HStack spacing={4} flexWrap="wrap">
                    <Checkbox isChecked={formData.empFITA} onChange={(e) => set("empFITA", e.target.checked)}>F/T</Checkbox>
                    <Checkbox isChecked={formData.empPTA} onChange={(e) => set("empPTA", e.target.checked)}>P/T</Checkbox>
                    <Checkbox isChecked={formData.empCasualA} onChange={(e) => set("empCasualA", e.target.checked)}>Casual</Checkbox>
                    <Checkbox isChecked={formData.empSelfA} onChange={(e) => set("empSelfA", e.target.checked)}>Self-Employed</Checkbox>
                  </HStack>
                </FormControl>
                <Grid templateColumns="1fr 1fr" gap={2}>
                  <LI label="Years in Current Role:" field="employmentYearsA" />
                  <LI label="Gross Income P/A $:" field="grossIncomeA" placeholder="$" />
                </Grid>
                <Grid templateColumns="1fr 1fr" gap={2}>
                  <LI label="Other Income $:" field="otherIncomeA" placeholder="$" />
                  <LI label="Income Type:" field="otherIncomeTypeA" placeholder="e.g. Rental, Div." />
                </Grid>
                <Text fontSize="xs" fontWeight="bold" color={brandConfig.colors.text.secondary} mt={1}>If Self-Employed:</Text>
                <LI label="Business Name:" field="selfEmpBusinessNameA" />
                <Grid templateColumns="1fr 1fr" gap={2}>
                  <LI label="ABN:" field="selfEmpABNA" />
                  <LI label="Years in Business:" field="selfEmpYearsA" />
                </Grid>
                <Grid templateColumns="1fr 1fr" gap={2}>
                  <LI label="Dependents:" field="dependentsA" />
                  <LI label="Ages:" field="dependentAgesA" />
                </Grid>
              </VStack>
            </GridItem>

            {/* Applicant B */}
            <GridItem>
              <Text fontWeight="bold" fontSize="sm" color={brandConfig.colors.primary} mb={3}>Applicant B</Text>
              <VStack spacing={3} align="stretch">
                <LI label="Full Name:" field="nameB" />
                <Grid templateColumns="1fr 1fr" gap={2}>
                  <LI label="Date of Birth:" field="dobB" placeholder="DD/MM/YYYY" />
                  <LI label="Marital Status:" field="maritalStatusB" />
                </Grid>
                <LI label="Residential Address:" field="addressB" />
                <Grid templateColumns="1fr 1fr 1fr" gap={2}>
                  <LI label="Yrs at Address:" field="residenceYearsB" />
                  <LI label="State:" field="stateB" />
                  <LI label="Postcode:" field="postcodeB" />
                </Grid>
                <Grid templateColumns="1fr 1fr" gap={2}>
                  <LI label="Phone (H):" field="phoneHomeB" />
                  <LI label="Mobile:" field="mobileB" />
                </Grid>
                <LI label="Email:" field="emailB" />
                <LI label="Tax File Number:" field="tfnB" />
                <LI label="Occupation:" field="occupationB" />
                <LI label="Employer:" field="employerB" />
                <LI label="Employer Address:" field="employerAddressB" />
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="bold">Employment Type:</FormLabel>
                  <HStack spacing={4} flexWrap="wrap">
                    <Checkbox isChecked={formData.empFITB} onChange={(e) => set("empFITB", e.target.checked)}>F/T</Checkbox>
                    <Checkbox isChecked={formData.empPTB} onChange={(e) => set("empPTB", e.target.checked)}>P/T</Checkbox>
                    <Checkbox isChecked={formData.empCasualB} onChange={(e) => set("empCasualB", e.target.checked)}>Casual</Checkbox>
                    <Checkbox isChecked={formData.empSelfB} onChange={(e) => set("empSelfB", e.target.checked)}>Self-Employed</Checkbox>
                  </HStack>
                </FormControl>
                <Grid templateColumns="1fr 1fr" gap={2}>
                  <LI label="Years in Current Role:" field="employmentYearsB" />
                  <LI label="Gross Income P/A $:" field="grossIncomeB" placeholder="$" />
                </Grid>
                <Grid templateColumns="1fr 1fr" gap={2}>
                  <LI label="Other Income $:" field="otherIncomeB" placeholder="$" />
                  <LI label="Income Type:" field="otherIncomeTypeB" placeholder="e.g. Rental, Div." />
                </Grid>
                <Text fontSize="xs" fontWeight="bold" color={brandConfig.colors.text.secondary} mt={1}>If Self-Employed:</Text>
                <LI label="Business Name:" field="selfEmpBusinessNameB" />
                <Grid templateColumns="1fr 1fr" gap={2}>
                  <LI label="ABN:" field="selfEmpABNB" />
                  <LI label="Years in Business:" field="selfEmpYearsB" />
                </Grid>
                <Grid templateColumns="1fr 1fr" gap={2}>
                  <LI label="Dependents:" field="dependentsB" />
                  <LI label="Ages:" field="dependentAgesB" />
                </Grid>
              </VStack>
            </GridItem>
          </Grid>

          <Divider my={4} />

          {/* ══════════════════════════════════════════════
              SECTION 2 — PROPERTY DETAILS
          ══════════════════════════════════════════════ */}
          <SH title="SECTION 2 — PROPERTY DETAILS" />
          <Grid templateColumns="1fr 1fr" gap={6} mb={4}>
            <VStack spacing={3} align="stretch">
              <LI label="Property Address:" field="propertyAddress" />
              <Grid templateColumns="1fr 1fr 1fr" gap={2}>
                <LI label="Suburb:" field="propertySuburb" />
                <LI label="State:" field="propertyState" />
                <LI label="Postcode:" field="propertyPostcode" />
              </Grid>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Property Type:</FormLabel>
                <HStack spacing={3} flexWrap="wrap">
                  <Checkbox isChecked={formData.isHouse} onChange={(e) => set("isHouse", e.target.checked)}>House</Checkbox>
                  <Checkbox isChecked={formData.isUnit} onChange={(e) => set("isUnit", e.target.checked)}>Unit</Checkbox>
                  <Checkbox isChecked={formData.isTownhouse} onChange={(e) => set("isTownhouse", e.target.checked)}>Townhouse</Checkbox>
                  <Checkbox isChecked={formData.isDuplex} onChange={(e) => set("isDuplex", e.target.checked)}>Duplex</Checkbox>
                  <Checkbox isChecked={formData.isLand} onChange={(e) => set("isLand", e.target.checked)}>Land</Checkbox>
                  <Checkbox isChecked={formData.isCommercial} onChange={(e) => set("isCommercial", e.target.checked)}>Commercial</Checkbox>
                </HStack>
              </FormControl>
              <Grid templateColumns="1fr 1fr 1fr" gap={2}>
                <LI label="Bedrooms:" field="bedrooms" />
                <LI label="Bathrooms:" field="bathrooms" />
                <LI label="Car Spaces:" field="carSpaces" />
              </Grid>
            </VStack>
            <VStack spacing={3} align="stretch">
              <Grid templateColumns="1fr 1fr" gap={2}>
                <LI label="Land Size (m²):" field="landSize" />
                <LI label="Floor Area (m²):" field="floorArea" />
              </Grid>
              <LI label="Year Built:" field="yearBuilt" />
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Body Corporate:</FormLabel>
                <HStack spacing={4}>
                  <Checkbox isChecked={formData.bodyCorpYes} onChange={(e) => set("bodyCorpYes", e.target.checked)}>YES</Checkbox>
                  <Checkbox isChecked={formData.bodyCorpNo} onChange={(e) => set("bodyCorpNo", e.target.checked)}>NO</Checkbox>
                </HStack>
              </FormControl>
              <LI label="Body Corp Fees P/Q $:" field="bodyCorpFees" placeholder="$" />
            </VStack>
          </Grid>

          <Divider my={4} />

          {/* ══════════════════════════════════════════════
              SECTION 3 — PURCHASE DETAILS & PURPOSE
          ══════════════════════════════════════════════ */}
          <SH title="SECTION 3 — PURCHASE DETAILS &amp; PURPOSE" />
          <Grid templateColumns="1fr 1fr" gap={6} mb={4}>
            <VStack spacing={3} align="stretch">
              <LI label="Purchase Price $:" field="purchasePrice" placeholder="$" />
              <LI label="Deposit Amount $:" field="depositAmount" placeholder="$" />
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Deposit Paid:</FormLabel>
                <HStack spacing={4}>
                  <Checkbox isChecked={formData.depositPaidYes} onChange={(e) => set("depositPaidYes", e.target.checked)}>YES</Checkbox>
                  <Checkbox isChecked={formData.depositPaidNo} onChange={(e) => set("depositPaidNo", e.target.checked)}>NO</Checkbox>
                </HStack>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Deposit Source:</FormLabel>
                <HStack spacing={3} flexWrap="wrap">
                  <Checkbox isChecked={formData.depositCash} onChange={(e) => set("depositCash", e.target.checked)}>Savings</Checkbox>
                  <Checkbox isChecked={formData.depositEquity} onChange={(e) => set("depositEquity", e.target.checked)}>Equity</Checkbox>
                  <Checkbox isChecked={formData.depositFHSS} onChange={(e) => set("depositFHSS", e.target.checked)}>FHSS</Checkbox>
                  <Checkbox isChecked={formData.depositGift} onChange={(e) => set("depositGift", e.target.checked)}>Gift</Checkbox>
                </HStack>
              </FormControl>
              <Grid templateColumns="1fr 1fr" gap={2}>
                <LI label="Exchange Date:" field="exchangeDate" placeholder="DD/MM/YYYY" />
                <LI label="Settlement Date:" field="settlementDate" placeholder="DD/MM/YYYY" />
              </Grid>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">First Home Buyer:</FormLabel>
                <HStack spacing={4}>
                  <Checkbox isChecked={formData.firstHomeBuyerYes} onChange={(e) => set("firstHomeBuyerYes", e.target.checked)}>YES</Checkbox>
                  <Checkbox isChecked={formData.firstHomeBuyerNo} onChange={(e) => set("firstHomeBuyerNo", e.target.checked)}>NO</Checkbox>
                </HStack>
              </FormControl>
            </VStack>
            <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Property Purpose:</FormLabel>
                <VStack align="start" spacing={2}>
                  <Checkbox isChecked={formData.purposePPOR} onChange={(e) => set("purposePPOR", e.target.checked)}>Owner-Occupied (PPOR)</Checkbox>
                  <Checkbox isChecked={formData.purposeDirectInvestment} onChange={(e) => set("purposeDirectInvestment", e.target.checked)}>Investment — Direct</Checkbox>
                  <Checkbox isChecked={formData.purposeSMSF} onChange={(e) => set("purposeSMSF", e.target.checked)}>Investment — SMSF</Checkbox>
                </VStack>
              </FormControl>
              <LI label="Estimated Weekly Rental $:" field="estimatedRental" placeholder="$" />
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Property Manager Engaged:</FormLabel>
                <HStack spacing={4}>
                  <Checkbox isChecked={formData.propertyManagedYes} onChange={(e) => set("propertyManagedYes", e.target.checked)}>YES</Checkbox>
                  <Checkbox isChecked={formData.propertyManagedNo} onChange={(e) => set("propertyManagedNo", e.target.checked)}>NO</Checkbox>
                </HStack>
              </FormControl>
              <LI label="Property Manager Name:" field="propertyManagerName" />
              <LI label="Property Manager Phone:" field="propertyManagerPhone" />
            </VStack>
          </Grid>

          <Divider my={4} />

          {/* ══════════════════════════════════════════════
              SECTION 4 — PROFESSIONAL CONTACTS
          ══════════════════════════════════════════════ */}
          <SH title="SECTION 4 — PROFESSIONAL CONTACTS" />
          <Grid templateColumns="1fr 1fr" gap={6} mb={4}>
            <VStack spacing={3} align="stretch">
              <Text fontWeight="bold" fontSize="xs" color={brandConfig.colors.primary}>Real Estate Agent</Text>
              <LI label="Agent Name:" field="agentName" />
              <LI label="Agency:" field="agentAgency" />
              <Grid templateColumns="1fr 1fr" gap={2}>
                <LI label="Phone:" field="agentPhone" />
                <LI label="Email:" field="agentEmail" />
              </Grid>
            </VStack>
            <VStack spacing={3} align="stretch">
              <Text fontWeight="bold" fontSize="xs" color={brandConfig.colors.primary}>Solicitor / Conveyancer</Text>
              <LI label="Name:" field="solicitorName" />
              <LI label="Firm:" field="solicitorFirm" />
              <Grid templateColumns="1fr 1fr" gap={2}>
                <LI label="Phone:" field="solicitorPhone" />
                <LI label="Email:" field="solicitorEmail" />
              </Grid>
            </VStack>
          </Grid>

          <Divider my={4} />

          {/* ══════════════════════════════════════════════
              SECTION 5 — FINANCE & LOAN DETAILS
          ══════════════════════════════════════════════ */}
          <SH title="SECTION 5 — FINANCE &amp; LOAN DETAILS" />
          <Grid templateColumns="1fr 1fr" gap={6} mb={4}>
            <VStack spacing={3} align="stretch">
              <Grid templateColumns="1fr 1fr" gap={2}>
                <LI label="Loan Amount Requested $:" field="loanAmountRequested" placeholder="$" />
                <LI label="LVR %:" field="lvr" placeholder="%" />
              </Grid>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Loan Purpose:</FormLabel>
                <HStack spacing={4}>
                  <Checkbox isChecked={formData.loanPurposeOO} onChange={(e) => set("loanPurposeOO", e.target.checked)}>Owner-Occupied</Checkbox>
                  <Checkbox isChecked={formData.loanPurposeInvestment} onChange={(e) => set("loanPurposeInvestment", e.target.checked)}>Investment</Checkbox>
                </HStack>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Repayment Type:</FormLabel>
                <HStack spacing={4}>
                  <Checkbox isChecked={formData.loanTypePI} onChange={(e) => set("loanTypePI", e.target.checked)}>Principal &amp; Interest</Checkbox>
                  <Checkbox isChecked={formData.loanTypeIO} onChange={(e) => set("loanTypeIO", e.target.checked)}>Interest Only</Checkbox>
                </HStack>
              </FormControl>
              <Grid templateColumns="1fr 1fr" gap={2}>
                <LI label="I/O Period (yrs):" field="ioPeriod" />
                <LI label="Loan Term (yrs):" field="loanTerm" />
              </Grid>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Interest Rate Type:</FormLabel>
                <HStack spacing={4}>
                  <Checkbox isChecked={formData.rateVariable} onChange={(e) => set("rateVariable", e.target.checked)}>Variable</Checkbox>
                  <Checkbox isChecked={formData.rateFixed} onChange={(e) => set("rateFixed", e.target.checked)}>Fixed</Checkbox>
                  <Checkbox isChecked={formData.rateSplit} onChange={(e) => set("rateSplit", e.target.checked)}>Split</Checkbox>
                </HStack>
              </FormControl>
              <LI label="Fixed Period (yrs):" field="fixedPeriod" />
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Offset Account:</FormLabel>
                <HStack spacing={4}>
                  <Checkbox isChecked={formData.offsetYes} onChange={(e) => set("offsetYes", e.target.checked)}>YES</Checkbox>
                  <Checkbox isChecked={formData.offsetNo} onChange={(e) => set("offsetNo", e.target.checked)}>NO</Checkbox>
                </HStack>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Redraw Facility:</FormLabel>
                <HStack spacing={4}>
                  <Checkbox isChecked={formData.redrawYes} onChange={(e) => set("redrawYes", e.target.checked)}>YES</Checkbox>
                  <Checkbox isChecked={formData.redrawNo} onChange={(e) => set("redrawNo", e.target.checked)}>NO</Checkbox>
                </HStack>
              </FormControl>
              <LI label="Lender Preference:" field="lenderPreference" />
            </VStack>
            <VStack spacing={3} align="stretch">
              <Text fontWeight="bold" fontSize="xs" color={brandConfig.colors.primary}>Broker Details</Text>
              <LI label="Broker Name:" field="brokerName" />
              <LI label="Company:" field="brokerCompany" />
              <LI label="Phone:" field="brokerPhone" />
              <LI label="ACL No.:" field="brokerACL" />
              <Divider />
              <Text fontWeight="bold" fontSize="xs" color={brandConfig.colors.primary}>Credit History</Text>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Any Defaults / Judgements:</FormLabel>
                <HStack spacing={4}>
                  <Checkbox isChecked={formData.defaultsYes} onChange={(e) => set("defaultsYes", e.target.checked)}>YES</Checkbox>
                  <Checkbox isChecked={formData.defaultsNo} onChange={(e) => set("defaultsNo", e.target.checked)}>NO</Checkbox>
                </HStack>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs">If YES, provide details:</FormLabel>
                <Textarea value={formData.defaultDetails} onChange={(e) => set("defaultDetails", e.target.value)} {...inp} size="sm" rows={2} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Bankruptcy / Part IX:</FormLabel>
                <HStack spacing={4}>
                  <Checkbox isChecked={formData.bankruptcyYes} onChange={(e) => set("bankruptcyYes", e.target.checked)}>YES</Checkbox>
                  <Checkbox isChecked={formData.bankruptcyNo} onChange={(e) => set("bankruptcyNo", e.target.checked)}>NO</Checkbox>
                </HStack>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs">If YES, provide details:</FormLabel>
                <Textarea value={formData.bankruptcyDetails} onChange={(e) => set("bankruptcyDetails", e.target.value)} {...inp} size="sm" rows={2} />
              </FormControl>
            </VStack>
          </Grid>

          <Divider my={4} />

          {/* ══════════════════════════════════════════════
              SECTION 6 — SMSF DETAILS
          ══════════════════════════════════════════════ */}
          <SH title="SECTION 6 — SMSF DETAILS (Complete if SMSF purchase applies)" />
          <Grid templateColumns="1fr 1fr" gap={6} mb={4}>
            <VStack spacing={3} align="stretch">
              <LI label="SMSF Fund Name:" field="smsfFundName" />
              <Grid templateColumns="1fr 1fr" gap={2}>
                <LI label="Fund ABN:" field="smsfABN" />
                <LI label="Fund TFN:" field="smsfTFN" />
              </Grid>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Trustee Structure:</FormLabel>
                <HStack spacing={4}>
                  <Checkbox isChecked={formData.smsfTrusteeIndividual} onChange={(e) => set("smsfTrusteeIndividual", e.target.checked)}>Individual Trustees</Checkbox>
                  <Checkbox isChecked={formData.smsfTrusteeCorporate} onChange={(e) => set("smsfTrusteeCorporate", e.target.checked)}>Corporate Trustee</Checkbox>
                </HStack>
              </FormControl>
              <LI label="Corporate Trustee Name:" field="smsfCorpTrusteeName" />
              <LI label="Corporate Trustee ACN:" field="smsfCorpTrusteeACN" />
              <Divider />
              <Text fontWeight="bold" fontSize="xs" color={brandConfig.colors.primary}>Member 1</Text>
              <LI label="Name:" field="smsfMember1Name" />
              <Grid templateColumns="1fr 1fr" gap={2}>
                <LI label="DOB:" field="smsfMember1DOB" placeholder="DD/MM/YYYY" />
                <LI label="Member Balance $:" field="smsfMember1Balance" placeholder="$" />
              </Grid>
              <Text fontWeight="bold" fontSize="xs" color={brandConfig.colors.primary}>Member 2</Text>
              <LI label="Name:" field="smsfMember2Name" />
              <Grid templateColumns="1fr 1fr" gap={2}>
                <LI label="DOB:" field="smsfMember2DOB" placeholder="DD/MM/YYYY" />
                <LI label="Member Balance $:" field="smsfMember2Balance" placeholder="$" />
              </Grid>
            </VStack>
            <VStack spacing={3} align="stretch">
              <Grid templateColumns="1fr 1fr" gap={2}>
                <LI label="Total Fund Balance $:" field="smsfTotalBalance" placeholder="$" />
                <LI label="Annual Contributions $:" field="smsfAnnualContributions" placeholder="$" />
              </Grid>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Limited Recourse Borrowing (LRBA):</FormLabel>
                <HStack spacing={4}>
                  <Checkbox isChecked={formData.smsfLRBAYes} onChange={(e) => set("smsfLRBAYes", e.target.checked)}>YES</Checkbox>
                  <Checkbox isChecked={formData.smsfLRBANo} onChange={(e) => set("smsfLRBANo", e.target.checked)}>NO</Checkbox>
                </HStack>
              </FormControl>
              <LI label="Bare Trust Name:" field="smsfBareTrustName" />
              <LI label="Fund Auditor Name:" field="smsfAuditorName" />
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Fund Currently Compliant (ATO):</FormLabel>
                <HStack spacing={4}>
                  <Checkbox isChecked={formData.smsfCompliantYes} onChange={(e) => set("smsfCompliantYes", e.target.checked)}>YES</Checkbox>
                  <Checkbox isChecked={formData.smsfCompliantNo} onChange={(e) => set("smsfCompliantNo", e.target.checked)}>NO</Checkbox>
                </HStack>
              </FormControl>
            </VStack>
          </Grid>

          <Divider my={4} />

          {/* ══════════════════════════════════════════════
              SECTION 7 — ASSETS
          ══════════════════════════════════════════════ */}
          <SH title="SECTION 7 — ASSETS" />
          <TableContainer mb={4}>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th {...thS} w="150px">Asset</Th>
                  <Th {...thS} w="120px">Value $</Th>
                  <Th {...thS}>Owner / Title</Th>
                  <Th {...thS} w="110px">Rental p.w. $</Th>
                </Tr>
              </Thead>
              <Tbody>
                <AssetRow label="PPOR / Home" vf="assetPPORValue" of="assetPPOROwner" rf="assetPPORRent" />
                <AssetRow label="Investment Prop. 1" vf="assetIP1Value" of="assetIP1Owner" rf="assetIP1Rent" />
                <AssetRow label="Investment Prop. 2" vf="assetIP2Value" of="assetIP2Owner" rf="assetIP2Rent" />
                <AssetRow label="Investment Prop. 3" vf="assetIP3Value" of="assetIP3Owner" rf="assetIP3Rent" />
                <AssetRow label="SMSF Property" vf="assetSMSFPropValue" of="assetSMSFPropOwner" />
                <AssetRow label="Superannuation" vf="assetSuperValue" of="assetSuperOwner" />
                <AssetRow label="Shares / Managed Funds" vf="assetSharesValue" of="assetSharesOwner" />
                <AssetRow label="Savings / Cash" vf="assetSavingsValue" of="assetSavingsOwner" />
                <AssetRow label="Vehicle 1" vf="assetVehicle1Value" of="assetVehicle1Owner" />
                <AssetRow label="Vehicle 2" vf="assetVehicle2Value" of="assetVehicle2Owner" />
                <AssetRow label="Business Interests" vf="assetBusinessValue" of="assetBusinessOwner" />
                <AssetRow label="Other" vf="assetOther1Value" of="assetOther1Owner" />
                <AssetRow label="Other" vf="assetOther2Value" of="assetOther2Owner" />
              </Tbody>
            </Table>
          </TableContainer>

          <Divider my={4} />

          {/* ══════════════════════════════════════════════
              SECTION 8 — LIABILITIES & LIVING EXPENSES
          ══════════════════════════════════════════════ */}
          <SH title="SECTION 8 — LIABILITIES &amp; LIVING EXPENSES" />
          <TableContainer mb={4}>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th {...thS} w="140px">Liability</Th>
                  <Th {...thS}>Lender</Th>
                  <Th {...thS} w="100px">Balance $</Th>
                  <Th {...thS} w="110px">Limit / Credit $</Th>
                  <Th {...thS} w="110px">Monthly Repmt $</Th>
                </Tr>
              </Thead>
              <Tbody>
                <LiabRow label="Mortgage 1" lf="liabMortgage1Lender" bf="liabMortgage1Balance" limf="liabMortgage1Limit" rf="liabMortgage1Repayment" />
                <LiabRow label="Mortgage 2" lf="liabMortgage2Lender" bf="liabMortgage2Balance" limf="liabMortgage2Limit" rf="liabMortgage2Repayment" />
                <LiabRow label="Mortgage 3" lf="liabMortgage3Lender" bf="liabMortgage3Balance" limf="liabMortgage3Limit" rf="liabMortgage3Repayment" />
                <LiabRow label="Car Loan" lf="liabCarLender" bf="liabCarBalance" limf="liabCarLimit" rf="liabCarRepayment" />
                <LiabRow label="Personal Loan" lf="liabPersonalLender" bf="liabPersonalBalance" limf="liabPersonalLimit" rf="liabPersonalRepayment" />
                <LiabRow label="Credit Card 1" lf="liabCC1Lender" bf="liabCC1Balance" limf="liabCC1Limit" rf="liabCC1Repayment" />
                <LiabRow label="Credit Card 2" lf="liabCC2Lender" bf="liabCC2Balance" limf="liabCC2Limit" rf="liabCC2Repayment" />
                {/* HECS / HELP — no lender/limit */}
                <Tr>
                  <Td {...tdL}>HECS / HELP Debt</Td>
                  <Td {...tdI} colSpan={2}><Input value={formData.liabHECSBalance} onChange={(e) => set("liabHECSBalance", e.target.value)} {...inp} size="xs" placeholder="Balance $" /></Td>
                  <Td {...tdI}><Box h="24px" bg="gray.100" borderRadius="md" /></Td>
                  <Td {...tdI}><Input value={formData.liabHECSRepayment} onChange={(e) => set("liabHECSRepayment", e.target.value)} {...inp} size="xs" placeholder="$ p/mth" /></Td>
                </Tr>
                {/* Expenses-only rows */}
                <Tr>
                  <Td {...tdL}>Priv. Health Ins.</Td>
                  <Td {...tdI} colSpan={3}><Box h="24px" bg="gray.100" borderRadius="md" /></Td>
                  <Td {...tdI}><Input value={formData.liabPrivateHealthRepayment} onChange={(e) => set("liabPrivateHealthRepayment", e.target.value)} {...inp} size="xs" placeholder="$ p/mth" /></Td>
                </Tr>
                <Tr>
                  <Td {...tdL}>Priv. School Fees</Td>
                  <Td {...tdI} colSpan={3}><Box h="24px" bg="gray.100" borderRadius="md" /></Td>
                  <Td {...tdI}><Input value={formData.liabPrivateSchoolRepayment} onChange={(e) => set("liabPrivateSchoolRepayment", e.target.value)} {...inp} size="xs" placeholder="$ p/mth" /></Td>
                </Tr>
                <Tr>
                  <Td {...tdL}>Child Care</Td>
                  <Td {...tdI} colSpan={3}><Box h="24px" bg="gray.100" borderRadius="md" /></Td>
                  <Td {...tdI}><Input value={formData.liabChildCareRepayment} onChange={(e) => set("liabChildCareRepayment", e.target.value)} {...inp} size="xs" placeholder="$ p/mth" /></Td>
                </Tr>
                <LiabRow label="Other" lf="liabOther1Lender" bf="liabOther1Balance" limf="liabOther1Limit" rf="liabOther1Repayment" />
                <LiabRow label="Other" lf="liabOther2Lender" bf="liabOther2Balance" limf="liabOther2Limit" rf="liabOther2Repayment" />
              </Tbody>
            </Table>
          </TableContainer>

          {/* Living Expenses */}
          <Text fontWeight="bold" fontSize="sm" color={brandConfig.colors.primary} mb={2}>Monthly Living Expenses</Text>
          <TableContainer mb={4}>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr><Th {...thS}>Expense Category</Th><Th {...thS} w="200px">Monthly Amount $</Th></Tr>
              </Thead>
              <Tbody>
                <ExpRow label="Groceries / Food" rf="expGroceries" />
                <ExpRow label="Transport / Car Running" rf="expTransport" />
                <ExpRow label="Utilities (Elec, Gas, Water)" rf="expUtilities" />
                <ExpRow label="Phone / Internet" rf="expPhoneInternet" />
                <ExpRow label="Insurance (all policies)" rf="expInsurance" />
                <ExpRow label="Medical / Health" rf="expMedical" />
                <ExpRow label="Entertainment / Dining" rf="expEntertainment" />
                <ExpRow label="Education" rf="expEducation" />
                <ExpRow label="Other Living Expenses" rf="expOtherMonthly" />
              </Tbody>
            </Table>
          </TableContainer>

          <Divider my={4} />

          {/* ══════════════════════════════════════════════
              SECTION 9 — INSURANCE
          ══════════════════════════════════════════════ */}
          <SH title="SECTION 9 — INSURANCE" />
          <Grid templateColumns="1fr 1fr" gap={6} mb={4}>
            <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Building Insurance:</FormLabel>
                <HStack spacing={4}><Checkbox isChecked={formData.buildingInsYes} onChange={(e) => set("buildingInsYes", e.target.checked)}>YES</Checkbox><Checkbox isChecked={formData.buildingInsNo} onChange={(e) => set("buildingInsNo", e.target.checked)}>NO</Checkbox></HStack>
              </FormControl>
              <Grid templateColumns="1fr 1fr" gap={2}><LI label="Insurer:" field="buildingInsurer" /><LI label="Policy No.:" field="buildingPolicyNo" /></Grid>
              <LI label="Sum Insured $:" field="buildingSum" placeholder="$" />
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Contents Insurance:</FormLabel>
                <HStack spacing={4}><Checkbox isChecked={formData.contentsInsYes} onChange={(e) => set("contentsInsYes", e.target.checked)}>YES</Checkbox><Checkbox isChecked={formData.contentsInsNo} onChange={(e) => set("contentsInsNo", e.target.checked)}>NO</Checkbox></HStack>
              </FormControl>
              <Grid templateColumns="1fr 1fr" gap={2}><LI label="Insurer:" field="contentsInsurer" /><LI label="Policy No.:" field="contentsPolicyNo" /></Grid>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Landlord Insurance:</FormLabel>
                <HStack spacing={4}><Checkbox isChecked={formData.landlordInsYes} onChange={(e) => set("landlordInsYes", e.target.checked)}>YES</Checkbox><Checkbox isChecked={formData.landlordInsNo} onChange={(e) => set("landlordInsNo", e.target.checked)}>NO</Checkbox></HStack>
              </FormControl>
              <Grid templateColumns="1fr 1fr" gap={2}><LI label="Insurer:" field="landlordInsurer" /><LI label="Policy No.:" field="landlordPolicyNo" /></Grid>
            </VStack>
            <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Life Insurance:</FormLabel>
                <HStack spacing={4}><Checkbox isChecked={formData.lifeInsYes} onChange={(e) => set("lifeInsYes", e.target.checked)}>YES</Checkbox><Checkbox isChecked={formData.lifeInsNo} onChange={(e) => set("lifeInsNo", e.target.checked)}>NO</Checkbox></HStack>
              </FormControl>
              <Grid templateColumns="1fr 1fr" gap={2}><LI label="Insurer:" field="lifeInsurer" /><LI label="Sum Insured $:" field="lifeSumInsured" placeholder="$" /></Grid>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Income Protection:</FormLabel>
                <HStack spacing={4}><Checkbox isChecked={formData.incomeProtYes} onChange={(e) => set("incomeProtYes", e.target.checked)}>YES</Checkbox><Checkbox isChecked={formData.incomeProtNo} onChange={(e) => set("incomeProtNo", e.target.checked)}>NO</Checkbox></HStack>
              </FormControl>
              <LI label="Insurer:" field="incomeProtInsurer" />
            </VStack>
          </Grid>

          <Divider my={4} />

          {/* ══════════════════════════════════════════════
              SECTION 10 — UTILITIES & SOLAR CROSS-SELL
          ══════════════════════════════════════════════ */}
          <SH title="SECTION 10 — UTILITIES &amp; ENERGY" />
          <Grid templateColumns="1fr 1fr" gap={6} mb={4}>
            <VStack spacing={3} align="stretch">
              <LI label="Electricity Provider:" field="electricityProvider" />
              <LI label="Avg. Electricity Bill (monthly) $:" field="electricityBillMonthly" placeholder="$" />
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Water Filter Installed:</FormLabel>
                <HStack spacing={4}>
                  <Checkbox isChecked={formData.hasWaterFilter} onChange={(e) => set("hasWaterFilter", e.target.checked)}>YES — Has Water Filter</Checkbox>
                  <Checkbox isChecked={formData.noWaterFilter} onChange={(e) => set("noWaterFilter", e.target.checked)}>NO — Does Not Have</Checkbox>
                </HStack>
              </FormControl>
            </VStack>
          </Grid>

          {/* ── Solar Cross-Sell Prompt ── */}
          <Box bg="#C69214" border="2px solid" borderColor="#B07A28" borderRadius="md" p={5} mb={4}>
            <HStack mb={3} align="flex-start" spacing={3}>
              <Box as={FaSun} color="white" fontSize="24px" flexShrink={0} mt={1} />
              <Box>
                <Text color="white" fontWeight="bold" fontSize="md" mb={1}>
                  ☀️ CONSULTANT PROMPT — One Solar Australia Cross-Sell
                </Text>
                <Box bg="rgba(0,0,0,0.15)" borderRadius="md" p={3}>
                  <Text color="white" fontSize="sm" fontStyle="italic" lineHeight="tall">
                    "So many of our clients have been talking about getting solar through our sister company <Text as="span" fontWeight="bold">One Solar Australia</Text>.
                    I'd love to squeeze you into one of our solar consultant's schedules — there's a lot you could be saving and putting straight back into your wealth strategy.
                    Can I book a quick appointment for you?"
                  </Text>
                </Box>
              </Box>
            </HStack>
            <Grid templateColumns="1fr 1fr" gap={4}>
              <VStack spacing={3} align="stretch">
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="bold" color="white">Client Interested in Solar Consultation:</FormLabel>
                  <HStack spacing={4}>
                    <Checkbox colorScheme="whiteAlpha" isChecked={formData.solarInterestYes} onChange={(e) => set("solarInterestYes", e.target.checked)}><Text color="white" fontSize="sm">YES — Book Appointment</Text></Checkbox>
                    <Checkbox colorScheme="whiteAlpha" isChecked={formData.solarInterestNo} onChange={(e) => set("solarInterestNo", e.target.checked)}><Text color="white" fontSize="sm">NO — Not at this time</Text></Checkbox>
                  </HStack>
                </FormControl>
              </VStack>
              <VStack spacing={2} align="stretch">
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="bold" color="white">Name for Appointment:</FormLabel>
                  <Input value={formData.solarApptName} onChange={(e) => set("solarApptName", e.target.value)} bg="white" size="sm" borderColor="rgba(255,255,255,0.5)" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="bold" color="white">Phone:</FormLabel>
                  <Input value={formData.solarApptPhone} onChange={(e) => set("solarApptPhone", e.target.value)} bg="white" size="sm" borderColor="rgba(255,255,255,0.5)" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="bold" color="white">Preferred Time / Day:</FormLabel>
                  <Input value={formData.solarApptPreferredTime} onChange={(e) => set("solarApptPreferredTime", e.target.value)} bg="white" size="sm" borderColor="rgba(255,255,255,0.5)" placeholder="e.g. Weekday mornings" />
                </FormControl>
              </VStack>
            </Grid>
          </Box>

          <Divider my={4} />

          {/* ══════════════════════════════════════════════
              SECTION 11 — DOCUMENT CHECKLIST
          ══════════════════════════════════════════════ */}
          <SH title="SECTION 11 — DOCUMENT CHECKLIST" />
          <Grid templateColumns="1fr 1fr" gap={6} mb={4}>
            <VStack align="start" spacing={2}>
              <Text fontSize="xs" fontWeight="bold" color={brandConfig.colors.primary}>Income & Identity</Text>
              <Checkbox isChecked={formData.docPayslips} onChange={(e) => set("docPayslips", e.target.checked)} fontSize="sm">Last 2 payslips (both applicants)</Checkbox>
              <Checkbox isChecked={formData.doc2YrTaxReturns} onChange={(e) => set("doc2YrTaxReturns", e.target.checked)} fontSize="sm">Last 2 years tax returns + NOA</Checkbox>
              <Checkbox isChecked={formData.doc3MthBankStatements} onChange={(e) => set("doc3MthBankStatements", e.target.checked)} fontSize="sm">3 months bank statements</Checkbox>
              <Checkbox isChecked={formData.docIDVerified} onChange={(e) => set("docIDVerified", e.target.checked)} fontSize="sm">Identity verified (Passport / Driver's licence)</Checkbox>
              <Checkbox isChecked={formData.docCentrelink} onChange={(e) => set("docCentrelink", e.target.checked)} fontSize="sm">Centrelink statement (if applicable)</Checkbox>
              <Checkbox isChecked={formData.docRentalStatement} onChange={(e) => set("docRentalStatement", e.target.checked)} fontSize="sm">Rental statements (12 months)</Checkbox>
            </VStack>
            <VStack align="start" spacing={2}>
              <Text fontSize="xs" fontWeight="bold" color={brandConfig.colors.primary}>Property Documents</Text>
              <Checkbox isChecked={formData.docContractOfSale} onChange={(e) => set("docContractOfSale", e.target.checked)} fontSize="sm">Contract of Sale / Purchase</Checkbox>
              <Checkbox isChecked={formData.docBuildingReport} onChange={(e) => set("docBuildingReport", e.target.checked)} fontSize="sm">Building inspection report</Checkbox>
              <Checkbox isChecked={formData.docPestReport} onChange={(e) => set("docPestReport", e.target.checked)} fontSize="sm">Pest inspection report</Checkbox>
              <Checkbox isChecked={formData.docCouncilRates} onChange={(e) => set("docCouncilRates", e.target.checked)} fontSize="sm">Council rates notice</Checkbox>
              <Checkbox isChecked={formData.docBodyCorp} onChange={(e) => set("docBodyCorp", e.target.checked)} fontSize="sm">Body corporate statements (if applicable)</Checkbox>
              <Text fontSize="xs" fontWeight="bold" color={brandConfig.colors.primary} mt={2}>SMSF Documents</Text>
              <Checkbox isChecked={formData.docSmsfDeed} onChange={(e) => set("docSmsfDeed", e.target.checked)} fontSize="sm">SMSF Trust Deed (current)</Checkbox>
              <Checkbox isChecked={formData.docSmsfAuditLetter} onChange={(e) => set("docSmsfAuditLetter", e.target.checked)} fontSize="sm">Latest SMSF Audit Letter</Checkbox>
              <Checkbox isChecked={formData.docSmsfTrusteeResolution} onChange={(e) => set("docSmsfTrusteeResolution", e.target.checked)} fontSize="sm">Trustee Resolution to Borrow</Checkbox>
              <Checkbox isChecked={formData.docBareTrustDeed} onChange={(e) => set("docBareTrustDeed", e.target.checked)} fontSize="sm">Bare Trust Deed</Checkbox>
            </VStack>
          </Grid>

          <Divider my={4} />

          {/* ══════════════════════════════════════════════
              PRIVACY ACT & DECLARATIONS
          ══════════════════════════════════════════════ */}
          <Box p={4} bg={brandConfig.colors.background.surface} border="1px solid" borderColor="rgba(0,0,0,0.1)" borderRadius="md" mb={6}>
            <Text fontSize="sm" fontWeight="bold" mb={2}>PRIVACY ACT & DECLARATION</Text>
            <Text fontSize="xs" lineHeight="tall">
              I/We declare that the information provided in this form is true and correct to the best of my/our knowledge and belief.
              I/We authorise One Property Australia and its authorised representatives to collect, use, and disclose this information
              to lenders, mortgage brokers, legal practitioners, and other parties as necessary to assess, process, and complete this
              property transaction. All information will be handled in accordance with the Privacy Act 1988 (Cth).
            </Text>
          </Box>

          {/* SIGNATURES */}
          <Grid templateColumns="1fr 1fr" gap={6} mb={4}>
            <Box>
              <Text fontSize="xs" fontWeight="bold" mb={1}>Signature (Applicant A):</Text>
              <Box h="60px" bg={brandConfig.colors.background.surface} border="1px solid" borderColor="rgba(0,0,0,0.1)" borderRadius="md" />
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="bold" mb={1}>Signature (Applicant B):</Text>
              <Box h="60px" bg={brandConfig.colors.background.surface} border="1px solid" borderColor="rgba(0,0,0,0.1)" borderRadius="md" />
            </Box>
          </Grid>

          <Grid templateColumns="1fr 1fr 1fr" gap={4}>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold">Consultant Name:</FormLabel>
              <Input value={formData.consultantName} onChange={(e) => set("consultantName", e.target.value)} {...inp} size="sm" />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold">Consultant No. / ACL:</FormLabel>
              <Input value={formData.consultantNumber} onChange={(e) => set("consultantNumber", e.target.value)} {...inp} size="sm" />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold">Date:</FormLabel>
              <Input value={formData.date} onChange={(e) => set("date", e.target.value)} placeholder="DD/MM/YYYY" {...inp} size="sm" />
            </FormControl>
          </Grid>

        </Box>
      </Container>

      <Box className="no-print"><FooterWithFourColumns /></Box>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; padding: 0; }
          #ptf-printable { border: none !important; box-shadow: none !important; }
        }
      `}</style>
    </Box>
  );
};

export default PropertyTransactionForm;
